import React from 'react';
import {
  Building2,
  Check,
  FileText,
  Shield,
  User,
} from 'lucide-react';
import { FormChipGroup, FormInput, FormSelect, FormSection, FormTextarea } from '../../components/FormComponents';
import {
  FEE_FREQUENCY_OPTIONS as BASE_FEE_FREQUENCY_OPTIONS,
  LIGHTSPARK_ENTITY_OPTIONS as LIGHTSPARK_ENTITIES,
  optionWithPlaceholder,
} from '../../constants/contracts';

const NDA_STATUS_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'yes', label: 'Yes - NDA in place' },
  { value: 'no', label: 'No - NDA not required' },
  { value: 'in_progress', label: 'In Progress' },
];

const DATA_ACCESS_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'none', label: 'None' },
  { value: 'limited', label: 'Limited' },
  { value: 'full', label: 'Full Access' },
];

const SYSTEM_ACCESS_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'none', label: 'None' },
  { value: 'read_only', label: 'Read Only' },
  { value: 'read_write', label: 'Read/Write' },
];

const RISK_RATING_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const AGREEMENT_TYPE_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'services', label: 'Services Agreement' },
  { value: 'saas', label: 'SaaS / Subscription' },
  { value: 'license', label: 'License Agreement' },
  { value: 'consulting', label: 'Consulting Agreement' },
  { value: 'clickwrap', label: 'Click-through / Online Terms' },
  { value: 'other', label: 'Other' },
];

const FEE_FREQUENCY_OPTIONS = optionWithPlaceholder(BASE_FEE_FREQUENCY_OPTIONS, 'Select...');

const YES_NO_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

// Editable example templates for the heavy security textareas — same
// "start from an example" affordance the intake wizard uses.
const FIELD_EXAMPLES = {
  dataTypes: [
    {
      label: 'SaaS tool',
      text: 'Employee names and work emails for account provisioning; no customer data, no financial data, no source code.',
    },
    {
      label: 'Data processor',
      text: 'Customer transaction metadata (amounts, timestamps, counterparty identifiers) processed for reconciliation; no card numbers or bank credentials.',
    },
  ],
  dataRetention: [
    {
      label: 'Standard deletion',
      text: 'Vendor deletes all Lightspark data within 30 days of contract termination; backups age out within 90 days.',
    },
    {
      label: 'Unknown',
      text: 'Retention terms not yet confirmed with the vendor — flagging for review.',
    },
  ],
  systemAccessDescription: [
    {
      label: 'SSO + API',
      text: 'Read-only API access to [system] scoped to [data]; user access via Okta SSO with SCIM deprovisioning.',
    },
    {
      label: 'Repo access',
      text: 'Read access to specific GitHub repositories for the engagement; no production system or credential access.',
    },
  ],
};

function ExampleInserts({ name, value, onChange }) {
  const examples = FIELD_EXAMPLES[name];
  if (!examples) return null;
  return (
    <div style={{ marginTop: 6 }}>
      <span className="muted" style={{ fontSize: 11, marginRight: 6 }}>Start from an example:</span>
      {examples.map((ex) => (
        <button
          key={ex.label}
          type="button"
          className="btn ghost xs"
          style={{ marginRight: 4 }}
          title="Insert this template — edit before submitting"
          onClick={() => onChange({
            target: { name, value: value && value.trim() ? `${value.trim()}\n${ex.text}` : ex.text },
          })}
        >
          {ex.label}
        </button>
      ))}
    </div>
  );
}

// Labeled sub-group inside a form section — keeps related fields visually
// together instead of one undifferentiated grid.
function FieldGroup({ label, children, columns = 2 }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          color: 'var(--text-secondary)',
          marginBottom: 10,
        }}
      >
        {label}
      </div>
      <div className={columns === 1 ? 'form-grid cols-1' : 'form-grid'}>
        {children}
      </div>
    </div>
  );
}

export const REQUEST_FORM_STEPS = [
  { id: 1, title: 'Vendor Info', icon: Building2 },
  { id: 2, title: 'Lightspark Details', icon: User },
  { id: 3, title: 'Security Assessment', icon: Shield },
  { id: 4, title: 'Contract Details', icon: FileText },
];

export function RequestFormStepIndicator({ steps, currentStep, onJump }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 32,
        gap: 8,
      }}
    >
      {steps.map((step, index) => {
        const Icon = step.icon;
        const isActive = currentStep === step.id;
        const isCompleted = currentStep > step.id;
        const isClickable = isCompleted;
        const bubbleBg = isCompleted
          ? 'var(--color-green-500)'
          : isActive
          ? 'var(--color-gray-950)'
          : 'var(--surface-alpha-tertiary)';
        const bubbleColor = isCompleted || isActive
          ? 'var(--text-inverse)'
          : 'var(--text-tertiary)';

        return (
          <React.Fragment key={step.id}>
            <button
              type="button"
              onClick={() => isClickable && onJump?.(step.id)}
              disabled={!isClickable}
              aria-label={`Go to step ${step.id}: ${step.title}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: isClickable ? 'pointer' : 'default',
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 999,
                  background: bubbleBg,
                  color: bubbleColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 140ms',
                }}
              >
                {isCompleted ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: '0.2px',
                  color: isActive
                    ? 'var(--text-primary)'
                    : isCompleted
                    ? 'var(--text-secondary)'
                    : 'var(--text-tertiary)',
                }}
              >
                {step.title}
              </span>
            </button>
            {index < steps.length - 1 && (
              <div
                style={{
                  width: 56,
                  height: 0.5,
                  margin: '0 4px',
                  background: currentStep > step.id
                    ? 'var(--color-green-500)'
                    : 'var(--border-primary)',
                  alignSelf: 'flex-start',
                  marginTop: 16,
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function RequestFormStepContent({
  currentStep,
  formData,
  onChange,
  onBlur,
  errors = {},
  touched = {},
}) {
  switch (currentStep) {
    case 1:
      return (
        <FormSection title="Vendor Information" icon={Building2}>
          <div className="stack-lg">
            <FormInput
              label="Vendor Name"
              name="vendorName"
              value={formData.vendorName}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Enter vendor company name..."
              icon={Building2}
              required
              error={touched.vendorName && errors.vendorName}
            />
            <div className="form-grid">
              <FormInput
                label="Vendor Website / Domain"
                name="vendorWebsite"
                value={formData.vendorWebsite}
                onChange={onChange}
                placeholder="https://vendor.com"
              />
              <FormInput
                label="Vendor Contact Name"
                name="vendorContactName"
                value={formData.vendorContactName}
                onChange={onChange}
                placeholder="Primary contact..."
              />
              <FormInput
                label="Vendor Email"
                name="vendorEmail"
                type="email"
                value={formData.vendorEmail}
                onChange={onChange}
                onBlur={onBlur}
                placeholder="contact@vendor.com"
                error={touched.vendorEmail && errors.vendorEmail}
              />
              <FormInput
                label="Vendor Address"
                name="vendorAddress"
                value={formData.vendorAddress}
                onChange={onChange}
                placeholder="Business address..."
              />
            </div>
          </div>
        </FormSection>
      );

    case 2:
      return (
        <FormSection title="Lightspark Details" icon={User}>
          <div className="stack-lg">
            <div className="form-grid">
              <FormSelect
                label="Lightspark Entity"
                name="lightsparkEntity"
                value={formData.lightsparkEntity}
                onChange={onChange}
                onBlur={onBlur}
                options={LIGHTSPARK_ENTITIES}
                icon={Building2}
                required
                error={touched.lightsparkEntity && errors.lightsparkEntity}
              />
              <FormInput
                label="Service Owner"
                name="serviceOwner"
                value={formData.serviceOwner}
                onChange={onChange}
                onBlur={onBlur}
                placeholder="Name of person responsible..."
                icon={User}
                required
                error={touched.serviceOwner && errors.serviceOwner}
              />
            </div>
            <FormInput
              label="Scope of Services"
              name="scope"
              value={formData.scope}
              onChange={onChange}
              placeholder="Brief description of what the vendor will provide..."
            />
            <FormTextarea
              label="Service Description"
              name="serviceDescription"
              value={formData.serviceDescription}
              onChange={onChange}
              rows={4}
              placeholder="Detailed description of the services..."
            />
          </div>
        </FormSection>
      );

    case 3:
      return (
        <FormSection title="Data & Security Assessment" icon={Shield}>
          <div className="stack-lg">
            <div className="form-grid">
              <FormChipGroup label="NDA Status" name="ndaStatus" value={formData.ndaStatus} onChange={onChange} options={NDA_STATUS_OPTIONS} />
              <FormChipGroup label="Sharing Confidential Info?" name="confidentialInfoShared" value={formData.confidentialInfoShared} onChange={onChange} options={YES_NO_OPTIONS} />
              <FormChipGroup label="Uses AI/ML?" name="aiMlUsage" value={formData.aiMlUsage} onChange={onChange} options={YES_NO_OPTIONS} />
              {formData.aiMlUsage === 'yes' && (
                <FormChipGroup label="Uses Lightspark Data for Training?" name="aiTrainingUse" value={formData.aiTrainingUse} onChange={onChange} options={YES_NO_OPTIONS} />
              )}
              <FormChipGroup label="Customer Interaction?" name="customerInteraction" value={formData.customerInteraction} onChange={onChange} options={YES_NO_OPTIONS} />
              <FormChipGroup label="Critical to Operations?" name="criticalToOperations" value={formData.criticalToOperations} onChange={onChange} options={YES_NO_OPTIONS} />
              <FormChipGroup label="Related Party?" name="relatedParty" value={formData.relatedParty} onChange={onChange} options={YES_NO_OPTIONS} />
              {formData.aiMlUsage === 'yes' && (
                <FormChipGroup label="Generates Code/IP?" name="aiCodeOrIpGeneration" value={formData.aiCodeOrIpGeneration} onChange={onChange} options={YES_NO_OPTIONS} />
              )}
              <FormChipGroup label="Data Access Level" name="dataAccessLevel" value={formData.dataAccessLevel} onChange={onChange} options={DATA_ACCESS_OPTIONS} />
              <FormChipGroup label="System Access" name="systemAccess" value={formData.systemAccess} onChange={onChange} options={SYSTEM_ACCESS_OPTIONS} />
              <FormChipGroup label="IP Production?" name="ipProduction" value={formData.ipProduction} onChange={onChange} options={YES_NO_OPTIONS} />
              <FormChipGroup
                label="Your Risk Estimate"
                name="riskRating"
                value={formData.riskRating}
                onChange={onChange}
                options={RISK_RATING_OPTIONS}
                hint="Optional gut check — the AI first-pass recommends its own rating after submission."
              />
            </div>
            <div>
              <FormTextarea
                label="Data Shared / Processed"
                name="dataTypes"
                value={formData.dataTypes}
                onChange={onChange}
                rows={3}
                placeholder="Customer data, employee data, source code, credentials, financial data, confidential info..."
              />
              <ExampleInserts name="dataTypes" value={formData.dataTypes} onChange={onChange} />
            </div>
            <div>
              <FormTextarea
                label="Data Retention / Deletion"
                name="dataRetention"
                value={formData.dataRetention}
                onChange={onChange}
                rows={3}
                placeholder="Describe whether vendor deletes data after the contract ends and any retention period..."
              />
              <ExampleInserts name="dataRetention" value={formData.dataRetention} onChange={onChange} />
            </div>
            {formData.systemAccess && formData.systemAccess !== 'none' && (
              <div>
                <FormTextarea
                  label="System Access Details"
                  name="systemAccessDescription"
                  value={formData.systemAccessDescription}
                  onChange={onChange}
                  rows={3}
                  placeholder="Describe any access to Lightspark systems, tools, repositories, accounts, or integrations..."
                />
                <ExampleInserts name="systemAccessDescription" value={formData.systemAccessDescription} onChange={onChange} />
              </div>
            )}
          </div>
        </FormSection>
      );

    case 4:
      return (
        <FormSection title="Contract Details" icon={FileText}>
          <div style={{ display: 'grid', gap: 24 }}>
            <FieldGroup label="Agreement">
              <FormSelect label="Agreement Type" name="agreementType" value={formData.agreementType} onChange={onChange} options={AGREEMENT_TYPE_OPTIONS} />
              {formData.agreementType === 'other' && (
                <FormInput
                  label="Other Agreement Type"
                  name="agreementTypeOther"
                  value={formData.agreementTypeOther}
                  onChange={onChange}
                  placeholder="Specify agreement type..."
                />
              )}
              <FormChipGroup label="Vendor Form / Paper?" name="vendorForm" value={formData.vendorForm} onChange={onChange} options={YES_NO_OPTIONS} hint="Will the contract be on the vendor's template rather than ours?" />
            </FieldGroup>

            <FieldGroup label="Fees & approval">
              <FormInput label="Estimated Fees" name="estimatedFees" type="number" value={formData.estimatedFees} onChange={onChange} placeholder="0.00" min="0" step="0.01" />
              <FormSelect label="Fee Frequency" name="feeFrequency" value={formData.feeFrequency} onChange={onChange} options={FEE_FREQUENCY_OPTIONS} />
              <FormChipGroup label="Expense Reimbursement?" name="expenseReimbursement" value={formData.expenseReimbursement} onChange={onChange} options={YES_NO_OPTIONS} />
              <FormChipGroup label="Requires CEO Approval?" name="requiresCeoApproval" value={formData.requiresCeoApproval} onChange={onChange} options={YES_NO_OPTIONS} hint="Required for engagements over $100k." />
              {formData.requiresCeoApproval === 'yes' && (
                <FormInput label="CEO Approval Evidence" name="ceoApprovalEvidence" value={formData.ceoApprovalEvidence} onChange={onChange} placeholder="Link, Slack thread, or note..." />
              )}
            </FieldGroup>

            <FieldGroup label="Term">
              <FormInput label="Start Date" name="startDate" type="date" value={formData.startDate} onChange={onChange} />
              <FormInput label="End Date" name="endDate" type="date" value={formData.endDate} onChange={onChange} />
            </FieldGroup>

            <FieldGroup label="Anything else" columns={1}>
              <FormTextarea
                label="Additional Notes"
                name="notes"
                value={formData.notes}
                onChange={onChange}
                rows={4}
                placeholder="Any additional information or context..."
              />
            </FieldGroup>
          </div>
        </FormSection>
      );

    default:
      return null;
  }
}
